import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import { useStudentProgress } from './useStudentProgress';
import { useModalNavigation } from './useModalNavigation';
import { useSubmissionState } from './useSubmissionState';
import { useAchievementState } from './useAchievementState';
import {
  // Only import actions that actually exist in authActions
} from '../../../actions/authActions';
import {
  addSubmission,
  updateSubmission,
  getUserSubmissionStatus
} from '../../../actions/submissionActions';

/**
 * Main hook for managing all StudentDashboard state and logic
 * Orchestrates all individual hooks and provides unified interface
 * @returns {Object} Complete dashboard state and handlers
 */
export const useStudentDashboard = () => {
  const dispatch = useDispatch();

  // Redux state selectors
  const user = useSelector(state => state.auth.user);
  const userProgress = useSelector(state => state.auth.progress);
  const navigationContext = useSelector(state => state.auth.navigationContext);
  const lastAction = useSelector(state => state.auth.lastAction);
  const courseDetails = useSelector(state => state.auth.courseDetails);

  // Derive pathCourses from user's path
  const pathCourses = user?.path?.courses || [];

  // Use new content-agnostic fields with fallback to legacy field
  const userCurrentContent = userProgress?.currentContent || userProgress?.currentLesson;
  const currentContentType = userProgress?.currentContentType ||
    (userCurrentContent ? 'lesson' : null); // fallback for legacy data

  /**
   * Find the course that contains the current content item
   * Supports all content types: lessons, submission forms, and reinforcements
   */
  const userCurrentCourse = userCurrentContent ?
    (() => {
      // Normalize content ID once to avoid repeated checks
      const contentId = typeof userCurrentContent === 'object'
        ? userCurrentContent._id
        : userCurrentContent;

      // Search through all courses in the learning path
      for (const course of pathCourses) {
        // Check if current content is in this course's lessons
        const isInLessons = course.lessons?.some(lesson => lesson._id === contentId);
        if (isInLessons) return course;

        // Check if current content is in this course's submission forms
        const isInSubmissionForms = course.submissionForms?.some(form => form._id === contentId);
        if (isInSubmissionForms) return course;

        // Check if current content is in this course's reinforcements
        const isInReinforcements = course.reinforcements?.some(reinforcement => reinforcement._id === contentId);
        if (isInReinforcements) return course;
      }

      return null;
    })()
    : null;

  // Get user submission status (for tracking submission form states)
  const userSubmissionStatus = useSelector(state => state.submission.userSubmissionStatus);

  // Import Redux actions at the top level - moved to import statement

  // Initialize all sub-hooks
  const progressHook = useStudentProgress(userProgress, pathCourses, userCurrentCourse, userCurrentContent);

  const modalHook = useModalNavigation(
    user,
    userCurrentCourse,
    userCurrentContent,
    currentContentType,
    navigationContext,
    pathCourses,
    userProgress,
    lastAction,
    progressHook.isPathCompletedLocal
  );

  const submissionHook = useSubmissionState(
    userCurrentContent,
    currentContentType,
    userSubmissionStatus,
    (data) => dispatch(addSubmission(data)),
    (id, data) => dispatch(updateSubmission(id, data))
  );

  const achievementHook = useAchievementState(
    user,
    userProgress,
    modalHook.showLessonModal,
    progressHook.isPathCompletedLocal
  );

  // Track submission status to prevent redundant fetches
  const fetchedStatusRef = useRef({});

  // Re-fetch submission status when modal opens (handles admin approval while student is away)
  useEffect(() => {
    if (modalHook.showLessonModal && userCurrentContent && currentContentType === 'submissionForm') {
      const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;
      const statusExists = userSubmissionStatus[contentId];

      // If status exists and is 'pending', always re-fetch when modal opens (admin may have approved)
      if (statusExists?.hasSubmission && statusExists.submission?.status === 'pending') {
        const userId = user?.id || user?._id;
        if (userId && contentId) {
          submissionHook.setSubmissionStatusLoading(true);
          dispatch(getUserSubmissionStatus(userId, contentId));
        }
      }
    }
  }, [modalHook.showLessonModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch submission status when navigating to a submission form
  useEffect(() => {
    if (userCurrentContent && currentContentType === 'submissionForm') {
      const contentId = typeof userCurrentContent === 'object' ? userCurrentContent._id : userCurrentContent;
      const statusExists = userSubmissionStatus[contentId];

      // If status already exists in Redux, don't do anything - just display it
      if (statusExists !== undefined) {
        // Make sure loading is cleared
        submissionHook.setSubmissionStatusLoading(false);
        return;
      }

      // Skip if already fetched for this session
      if (fetchedStatusRef.current[contentId]) {
        return;
      }

      const userId = user?.id || user?._id;
      if (userId && contentId) {
        fetchedStatusRef.current[contentId] = true;
        submissionHook.setSubmissionStatusLoading(true);
        dispatch(getUserSubmissionStatus(userId, contentId));
      }
    }
  }, [user, userCurrentContent, currentContentType, userSubmissionStatus, dispatch, submissionHook]);

  // Fetch submission status when viewing backwards to a submission form
  useEffect(() => {
    const nav = modalHook.modalNavigation;
    if (nav?.isViewingBackwards && nav.viewingContent && nav.viewingContentType === 'submissionForm') {
      const contentId = nav.viewingContent._id;
      const statusExists = userSubmissionStatus[contentId];

      if (statusExists !== undefined) return;
      if (fetchedStatusRef.current[contentId]) return;

      const userId = user?.id || user?._id;
      if (userId && contentId) {
        fetchedStatusRef.current[contentId] = true;
        dispatch(getUserSubmissionStatus(userId, contentId));
      }
    }
  }, [modalHook.modalNavigation, user, userSubmissionStatus, dispatch]);

  // Combine all state and handlers
  return {
    // Redux state
    user,
    courseDetails,
    userCurrentCourse,
    userCurrentContent,
    currentContentType,
    navigationContext,
    pathCourses,
    userProgress,
    lastAction,
    userSubmissionStatus,

    // Redux actions (only the ones that exist)
    dispatch,
    addSubmission: (data) => dispatch(addSubmission(data)),
    updateSubmission: (id, data) => dispatch(updateSubmission(id, data)),
    // Note: Other completion actions don't exist in current codebase
    // They may need to be implemented or handled differently

    // Progress hook state and functions
    ...progressHook,

    // Modal hook state and functions
    ...modalHook,

    // Submission hook state and functions
    ...submissionHook,

    // Achievement hook state and functions
    ...achievementHook
  };
};